import { BaseService } from './BaseService';
import { ValidationError, BusinessRuleError } from '../utils/errors';
import { Decimal } from 'decimal.js';
import {
  ReportTemplate,
  ReportData,
  FinancialStatement,
  Form990Data,
  ReportingContext
} from '../types/reporting';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { QueryResult, QueryResultRow, PoolClient } from 'pg';

// Define the DashboardMetrics interface locally since it's not exported from types/reporting
interface DashboardMetrics {
  revenue: number;
  expenses: number;
  revenueExpenseRatio: number;
  fundCount: number;
  fundBalance: number;
  grantCount: number;
  grantAmount: number;
  complianceAlertCount: number;
  transactionCount: number;
  projectCount: number;
  revenueTrend: Array<{ date: string; amount: number }>;
  expenseTrend: Array<{ date: string; amount: number }>;
}

// Define the FinancialTrends interface locally to avoid conflict
interface FinancialTrends {
  revenue_trend: Array<{ date: string; amount: number }>;
  expense_trend: Array<{ date: string; amount: number }>;
}

export class ReportingEngine extends BaseService {
  protected async validateReportingPeriod(context: ReportingContext): Promise<void> {
    if (!context.startDate || !context.endDate) {
      throw new ValidationError('Start date and end date are required');
    }
    if (context.endDate <= context.startDate) {
      throw new ValidationError('End date must be after start date');
    }
    // Add additional validation as needed
  }

  protected async getFinancialData(client: any, context: ReportingContext): Promise<any> {
    // Implement financial data retrieval
    return {};
  }

  protected async generateGrantReports(client: any, financialData: any, context: ReportingContext): Promise<ReportData> {
    // Implement grant report generation
    return { type: 'grant', data: {} };
  }

  protected async generateImpactReports(client: any, financialData: any, context: ReportingContext): Promise<ReportData> {
    // Implement impact report generation
    return { type: 'impact', data: {} };
  }

  protected async applyTemplate(reportData: ReportData, template: ReportTemplate, context: ReportingContext): Promise<ReportData> {
    // Implement template application
    return reportData;
  }

  protected async createReportingAuditTrail(client: any, context: ReportingContext, reportData: ReportData): Promise<void> {
    // Implement audit trail creation
  }

  private formatDashboardMetrics(data: any): DashboardMetrics {
    // Create a metrics object with a structure that matches DashboardMetrics
    return {
      revenue: data?.total_revenue || 0,
      expenses: data?.total_expenses || 0,
      revenueExpenseRatio: data?.revenue_expense_ratio || 0,
      fundCount: data?.active_funds || 0,
      fundBalance: data?.total_fund_balance || 0,
      grantCount: data?.active_grants || 0,
      grantAmount: data?.total_grant_amount || 0,
      complianceAlertCount: data?.compliance_alerts || 0,
      transactionCount: data?.transaction_count || 0,
      projectCount: data?.active_projects || 0,
      revenueTrend: data?.trends?.revenue || [],
      expenseTrend: data?.trends?.expenses || []
    };
  }

  protected async generateBalanceSheet(client: any, context: ReportingContext): Promise<any> {
    // Implement balance sheet generation
    return {};
  }

  protected async generateActivityStatement(client: any, context: ReportingContext): Promise<any> {
    // Implement activity statement generation
    return {};
  }

  protected async generateCashFlowStatement(client: any, context: ReportingContext): Promise<any> {
    // Implement cash flow statement generation
    return {};
  }

  protected async generateFunctionalExpenses(client: any, context: ReportingContext): Promise<any> {
    // Implement functional expenses generation
    return {};
  }

  protected async generateFinancialNotes(client: any, context: ReportingContext): Promise<any> {
    // Implement financial notes generation
    return {};
  }

  protected async runQuery<T extends QueryResultRow = any>(
    query: string,
    params: any[],
    client?: PoolClient
  ): Promise<QueryResult<T>> {
    if (client) {
      // If client is provided, we're in a transaction
      return await client.query(query, params);
    } else {
      // Otherwise use our BaseService executeQuery
      return await this.executeQuery(query, params);
    }
  }

  async generateReport(context: ReportingContext, config: any): Promise<ReportData> {
    return await this.executeTransaction(async (client) => {
      await this.validateReportingPeriod(context);
      const financialData = await this.getFinancialData(client, context);
      
      let reportData: ReportData = {
        type: context.reportType || 'financial',
        data: {}
      };

      switch (context.reportType) {
        case 'financial': {
          const financialStatement = await this.generateFinancialStatements(
            client,
            financialData,
            context
          );
          reportData.data = financialStatement;
          break;
        }
        case 'tax': {
          const taxData = await this.generateTaxReports(
            client,
            financialData,
            context
          );
          reportData.data = taxData;
          break;
        }
        case 'grant':
          reportData = await this.generateGrantReports(
            client,
            financialData,
            context
          );
          break;
        case 'impact':
          reportData = await this.generateImpactReports(
            client,
            financialData,
            context
          );
          break;
        case 'dashboard': {
          const metrics = await this.generateDashboardMetrics(
            client,
            financialData,
            context
          );
          reportData.data = metrics;
          break;
        }
      }

      if (config.template) {
        reportData = await this.applyTemplate(
          reportData,
          config.template,
          context
        );
      }

      await this.createReportingAuditTrail(
        client,
        context,
        reportData
      );

      return reportData;
    });
  }

  protected async executeTransaction<T>(callback: (client: PoolClient) => Promise<T>): Promise<T> {
    if (typeof window !== 'undefined') {
      // In browser environment, use API endpoint
      throw new Error('Transactions are not supported in browser environment yet. Please implement a transaction API endpoint.');
    } else {
      // In Node.js environment, use pg directly
      try {
        const { Pool } = await import('pg');
        const pool = new Pool({
          connectionString: process.env.DATABASE_URL,
          ssl: process.env.NODE_ENV === 'production' ? { rejectUnauthorized: false } : false
        });
        
        const client = await pool.connect();
        try {
          await client.query('BEGIN');
          const result = await callback(client);
          await client.query('COMMIT');
          return result;
        } catch (error) {
          await client.query('ROLLBACK');
          throw error;
        } finally {
          client.release();
        }
      } catch (error) {
        console.error('Transaction error:', error);
        throw error;
      }
    }
  }

  async generateFinancialStatements(
    client: PoolClient,
    financialData: any,
    context: ReportingContext
  ): Promise<FinancialStatement> {
    const balanceSheet = await this.generateBalanceSheet(client, context);
    const activityStatement = await this.generateActivityStatement(client, context);
    const cashFlowStatement = await this.generateCashFlowStatement(client, context);
    const functionalExpenses = await this.generateFunctionalExpenses(client, context);
    const notes = await this.generateFinancialNotes(client, context);
    
    return {
      balanceSheet,
      activityStatement,
      cashFlowStatement,
      functionalExpenses,
      notes,
      metadata: {
        generatedAt: new Date(),
        period: {
          start: context.startDate || new Date(),
          end: context.endDate || new Date()
        },
        customization: context.customization || {}
      }
    };
  }

  async generateTaxReports(
    client: PoolClient,
    financialData: any,
    context: ReportingContext
  ): Promise<Form990Data> {
    const query = `
      WITH tax_data AS (
        SELECT 
          -- Revenue
          COALESCE(SUM(CASE 
            WHEN a.type = 'revenue' AND a.category = 'contribution' THEN je.credit - je.debit
            ELSE 0 
          END), 0) as contributions_grants,
          
          COALESCE(SUM(CASE 
            WHEN a.type = 'revenue' AND a.category = 'program_service' THEN je.credit - je.debit
            ELSE 0 
          END), 0) as program_service_revenue,
          
          COALESCE(SUM(CASE 
            WHEN a.type = 'revenue' AND a.category = 'investment' THEN je.credit - je.debit
            ELSE 0 
          END), 0) as investment_income,
          
          -- Expenses
          COALESCE(SUM(CASE 
            WHEN a.type = 'expense' AND a.category = 'program_service' THEN je.debit - je.credit
            ELSE 0 
          END), 0) as program_service_expenses,
          
          COALESCE(SUM(CASE 
            WHEN a.type = 'expense' AND a.category = 'management' THEN je.debit - je.credit
            ELSE 0 
          END), 0) as management_expenses,
          
          COALESCE(SUM(CASE 
            WHEN a.type = 'expense' AND a.category = 'fundraising' THEN je.debit - je.credit
            ELSE 0 
          END), 0) as fundraising_expenses,
          
          -- Assets
          COALESCE(SUM(CASE 
            WHEN a.type = 'asset' AND a.category = 'current' THEN je.debit - je.credit
            ELSE 0 
          END), 0) as current_assets,
          
          COALESCE(SUM(CASE 
            WHEN a.type = 'asset' AND a.category = 'non_current' THEN je.debit - je.credit
            ELSE 0 
          END), 0) as non_current_assets,
          
          -- Liabilities
          COALESCE(SUM(CASE 
            WHEN a.type = 'liability' AND a.category = 'current' THEN je.credit - je.debit
            ELSE 0 
          END), 0) as current_liabilities,
          
          COALESCE(SUM(CASE 
            WHEN a.type = 'liability' AND a.category = 'non_current' THEN je.credit - je.debit
            ELSE 0 
          END), 0) as non_current_liabilities
          
        FROM sponsfi.journal_entries je
        JOIN sponsfi.accounts a ON je.account_id = a.id
        JOIN sponsfi.journals j ON je.journal_id = j.id
        WHERE j.fiscal_sponsor_id = $1
        AND j.entry_date BETWEEN $2 AND $3
      )
      SELECT 
        *,
        -- Calculated fields
        (contributions_grants + program_service_revenue + investment_income) as total_revenue,
        (program_service_expenses + management_expenses + fundraising_expenses) as total_expenses,
        (current_assets + non_current_assets) as total_assets,
        (current_liabilities + non_current_liabilities) as total_liabilities
      FROM tax_data
    `;

    interface TaxQueryResult extends QueryResultRow {
      contributions_grants: number;
      program_service_revenue: number;
      investment_income: number;
      program_service_expenses: number;
      management_expenses: number;
      fundraising_expenses: number;
      current_assets: number;
      non_current_assets: number;
      current_liabilities: number;
      non_current_liabilities: number;
      total_revenue: number;
      total_expenses: number;
      total_assets: number;
      total_liabilities: number;
    }

    const result = await this.runQuery<TaxQueryResult>(
      query,
      [context.fiscalSponsorId, context.startDate, context.endDate],
      client
    );

    const taxData = result.rows[0];

    return {
      fiscalYear: context.endDate?.getFullYear() || new Date().getFullYear(),
      ein: financialData.ein || '',
      organizationName: financialData.organizationName || '',
      revenue: {
        contributionsGrants: taxData.contributions_grants,
        programService: taxData.program_service_revenue,
        investment: taxData.investment_income,
        total: taxData.total_revenue
      },
      expenses: {
        programService: taxData.program_service_expenses,
        management: taxData.management_expenses,
        fundraising: taxData.fundraising_expenses,
        total: taxData.total_expenses
      },
      assets: {
        current: taxData.current_assets,
        nonCurrent: taxData.non_current_assets,
        total: taxData.total_assets
      },
      liabilities: {
        current: taxData.current_liabilities,
        nonCurrent: taxData.non_current_liabilities,
        total: taxData.total_liabilities
      },
      netAssets: taxData.total_assets - taxData.total_liabilities,
      metadata: {
        generatedAt: new Date(),
        period: {
          start: context.startDate || new Date(),
          end: context.endDate || new Date()
        }
      }
    };
  }

  async generateDashboardMetrics(
    client: PoolClient,
    financialData: any,
    context: ReportingContext
  ): Promise<DashboardMetrics> {
    const query = `
      WITH period_metrics AS (
        SELECT 
          -- Financial Health Metrics
          COALESCE(SUM(CASE 
            WHEN a.type = 'revenue' THEN je.credit - je.debit
            ELSE 0 
          END), 0) as total_revenue,
          COALESCE(SUM(CASE 
            WHEN a.type = 'expense' THEN je.debit - je.credit
            ELSE 0 
          END), 0) as total_expenses,
          
          -- Fund Metrics
          COUNT(DISTINCT f.id) as active_funds,
          SUM(f.current_balance) as total_fund_balance,
          
          -- Grant Metrics
          COUNT(DISTINCT g.id) as active_grants,
          SUM(g.amount) as total_grant_amount,
          
          -- Compliance Metrics
          COUNT(DISTINCT ca.id) as compliance_alerts,
          
          -- Transaction Metrics
          COUNT(DISTINCT j.id) as transaction_count,
          
          -- Project Metrics
          COUNT(DISTINCT p.id) as active_projects
          
        FROM sponsfi.journal_entries je
        JOIN sponsfi.accounts a ON je.account_id = a.id
        JOIN sponsfi.journals j ON je.journal_id = j.id
        LEFT JOIN sponsfi.funds f ON je.fund_id = f.id
        LEFT JOIN sponsfi.grants g ON je.grant_id = g.id
        LEFT JOIN sponsfi.compliance_alerts ca ON ca.fiscal_sponsor_id = j.fiscal_sponsor_id
        LEFT JOIN sponsfi.projects p ON je.project_id = p.id
        WHERE j.fiscal_sponsor_id = $1
        AND j.entry_date BETWEEN $2 AND $3
      )
      SELECT 
        pm.*,
        -- Calculated Metrics
        CASE 
          WHEN pm.total_expenses = 0 THEN NULL
          ELSE pm.total_revenue / pm.total_expenses 
        END as revenue_expense_ratio,
        
        -- Trend Analysis
        json_build_object(
          'revenue', (
            SELECT json_agg(json_build_object(
              'date', date_trunc('month', j.entry_date),
              'amount', SUM(je.credit - je.debit)
            ))
            FROM sponsfi.journal_entries je
            JOIN sponsfi.journals j ON je.journal_id = j.id
            JOIN sponsfi.accounts a ON je.account_id = a.id
            WHERE j.fiscal_sponsor_id = $1
            AND j.entry_date BETWEEN $2 AND $3
            AND a.type = 'revenue'
            GROUP BY date_trunc('month', j.entry_date)
            ORDER BY date_trunc('month', j.entry_date)
          ),
          'expenses', (
            SELECT json_agg(json_build_object(
              'date', date_trunc('month', j.entry_date),
              'amount', SUM(je.debit - je.credit)
            ))
            FROM sponsfi.journal_entries je
            JOIN sponsfi.journals j ON je.journal_id = j.id
            JOIN sponsfi.accounts a ON je.account_id = a.id
            WHERE j.fiscal_sponsor_id = $1
            AND j.entry_date BETWEEN $2 AND $3
            AND a.type = 'expense'
            GROUP BY date_trunc('month', j.entry_date)
            ORDER BY date_trunc('month', j.entry_date)
          )
        ) as trends
      FROM period_metrics
    `;
    
    const result = await this.runQuery(
      query,
      [context.fiscalSponsorId, context.startDate, context.endDate],
      client
    );
    
    const metricsData = result.rows[0];
    return this.formatDashboardMetrics(metricsData);
  }

  async getSummary(fiscalSponsorId: string) {
    return await this.withTransaction(async (client) => {
      const result = await this.runQuery(
        `SELECT 
          COUNT(*) as total_transactions,
          SUM(CASE WHEN type = 'income' THEN amount ELSE 0 END) as total_income,
          SUM(CASE WHEN type = 'expense' THEN amount ELSE 0 END) as total_expenses,
          COUNT(DISTINCT cause_id) as active_causes
        FROM transactions 
        WHERE fiscal_sponsor_id = $1 
        AND created_at >= NOW() - INTERVAL '30 days'`,
        [fiscalSponsorId],
        client
      );
      return result.rows[0];
    });
  }

  async getTrends(fiscalSponsorId: string) {
    return await this.withTransaction(async (client) => {
      const result = await this.runQuery(
        `SELECT 
          DATE_TRUNC('day', created_at) as date,
          type,
          SUM(amount) as total_amount,
          COUNT(*) as transaction_count
        FROM transactions 
        WHERE fiscal_sponsor_id = $1 
        AND created_at >= NOW() - INTERVAL '90 days'
        GROUP BY DATE_TRUNC('day', created_at), type
        ORDER BY date DESC`,
        [fiscalSponsorId],
        client
      );
      return result.rows;
    });
  }

  async getStatements(fiscalSponsorId: string) {
    return await this.withTransaction(async (client) => {
      const result = await this.runQuery(
        `SELECT * FROM statements 
         WHERE fiscal_sponsor_id = $1 
         ORDER BY period_end DESC 
         LIMIT 12`,
        [fiscalSponsorId],
        client
      );
      return result.rows;
    });
  }

  async getTaxDocuments(fiscalSponsorId: string) {
    return await this.withTransaction(async (client) => {
      const result = await this.runQuery(
        `SELECT * FROM tax_documents 
         WHERE fiscal_sponsor_id = $1 
         ORDER BY tax_year DESC, created_at DESC`,
        [fiscalSponsorId],
        client
      );
      return result.rows;
    });
  }

  public async getDashboardMetrics(filters = {}, context: ReportingContext): Promise<DashboardMetrics> {
    return await this.withTransaction(async (client) => {
      return await this.generateDashboardMetrics(client, filters, context);
    });
  }
}

export const useReportingEngine = () => {
  const { currentUser } = useAuth();
  const engine = useMemo(() => new ReportingEngine(), []);
  
  return {
    generateDashboardMetrics: useCallback(async (filters = {}) => {
      return await engine.getDashboardMetrics(filters, {
        fiscalSponsorId: currentUser.fiscalSponsorId,
        startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
        endDate: new Date(),
        userId: currentUser.id
      });
    }, [currentUser, engine]),

    getTrends: useCallback(async () => {
      return await engine.getTrends(currentUser.fiscalSponsorId);
    }, [currentUser, engine]),

    getStatements: useCallback(async () => {
      return await engine.getStatements(currentUser.fiscalSponsorId);
    }, [currentUser, engine])
  };
}; 